import 'rc-tooltip/assets/bootstrap_white.css';
import Tooltip from 'rc-tooltip';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Icon from '../common/Icon';
import theme from '../../utils/theme';
import PartOptions from '../common/PartOptions';
import ChordBarsChart from '../ChordBarsChart';
import IntensityMeter from '../IntensityMeter';
import { canArrange } from '../IntensityPanel';
import { isSectionPart, isPartInSection } from '../../utils/partNumbers';
import { hasMidi } from '../../utils/midiJson';
import { getPartGrid, hasPartChart, isChartDrawn } from '../../utils/partCharts';
import { getBeatsPerBar } from '../../utils/songTiming';
import { getInstrumentLabels } from '../../utils/parts';

import { ReactComponent as MusicIcon } from '../../icons/album-list.svg';
import { ReactComponent as AssignedIcon } from '../../icons/user.svg';
import { ReactComponent as InstrumentsIcon } from '../../icons/music-double-note.svg';
import { ReactComponent as PlayIcon } from '../../icons/play-solid.svg';
import { ReactComponent as RepeatIcon } from '../../icons/repeat.svg';
import { ReactComponent as NoteIcon } from '../../icons/notes.svg';
import { ReactComponent as CheckIcon } from '../../icons/check.svg';
import { ReactComponent as ChordBarsIcon } from '../../icons/chord-bars.svg';

// Tapping a part's name shows its instruments, its words, and an instrumental's chart under them.
// A part with none of these gets no tooltip rather than an empty bubble.
function PartTooltip({ part, chart, beatsPerBar, enabled, children }) {
  if (!enabled) return children;

  const instruments = getInstrumentLabels(part);
  const instrumentsLine = instruments.length > 0 && (
    <span className="instruments-tooltip">
      <InstrumentsIcon className="instruments-tooltip-icon" aria-hidden="true" />
      {instruments.join(' + ')}
    </span>
  );

  const overlay = chart ? (
    <div className="chart-tooltip">
      {instrumentsLine}
      {part.lyrics?.trim() && <span className="lyrics-tooltip">{part.lyrics}</span>}
      <ChordBarsChart
        lines={chart.lines}
        sections={chart.sections}
        beatsPerBar={beatsPerBar}
        countInEntry={chart.countInEntry}
      />
    </div>
  ) : (
    <div className="part-tooltip">
      {instrumentsLine}
      {part.lyrics?.trim() && <span className="lyrics-tooltip">{part.lyrics}</span>}
    </div>
  );

  // A chart needs the width a row's left edge doesn't have, so it drops below the name instead
  return (
    <Tooltip placement={chart ? 'bottomLeft' : 'left'} trigger={['click']} overlay={overlay}>
      {children}
    </Tooltip>
  );
}

function SongPart({
  part,
  parts,
  partIndex,
  displayNumber = part.number,
  onPartDelete,
  onPartDuplicate,
  onPartEdit,
  onPartIntensity,
  editPartId,
  isHighlighted,
  isDimmed,
  isCollapsed,
  isFirstVisible,
  isLastVisible,
  flashType,
  onBackgroundClick,
  onMenuOpenChange,
  attachedNoteCount = 0,
  attachedToCount = 0,
  canAttach = false,
  isAttached = false,
  onToggleAttach,
  timeSignature,
}) {
  const { t } = useTranslation();
  const isOtherPartBeingEdited = editPartId && editPartId !== part.id;
  const isSection = isSectionPart(part);
  const beatsPerBar = getBeatsPerBar(timeSignature);
  // An instrumental's chart is its body, as in View, so the tooltip shows it under any words it has
  const chart = isChartDrawn(part) ? getPartGrid(part, beatsPerBar) : null;
  const hasTooltip = Boolean(part.lyrics?.trim() || chart || part.assignedInstruments?.length);

  const partClasses = classNames('part', {
    'section-divider': isSection,
    // Editing dims every other row, but a row offering the Attach toggle is the thing being
    // chosen from — it has to stay legible
    dimmed: !canAttach && (isOtherPartBeingEdited || isDimmed),
    highlighted: isHighlighted,
    'part-collapsed': isCollapsed,
    'part-first': isFirstVisible,
    'part-last': isLastVisible,
    'flash-duplicate': flashType === 'duplicate',
    'flash-delete': flashType === 'delete',
  });

  const handleClick = (e) => {
    // Only react to clicks on the row's empty background, not on the name/icons/controls above it
    if (e.target === e.currentTarget) onBackgroundClick?.();
  };

  return (
    <li className={theme.renderPartColor(part.name, partClasses)} onClick={handleClick}>
      <span className="info" onClick={isSection ? () => onBackgroundClick?.() : undefined}>
        <Icon icon={<MusicIcon />} className="drag-handle" />

        {isSection ? (
          <span className="name section-name">
            <span className="name-text">{part.lyrics}</span>
          </span>
        ) : (
          <PartTooltip part={part} chart={chart} beatsPerBar={beatsPerBar} enabled={hasTooltip}>
            <span className="name clickable">
              <span className="name-text">
                {t(`parts.${part.name}`, { defaultValue: part.name })} {displayNumber}
              </span>
              {part.repeatCount > 1 && <span className="repeat-badge">×{part.repeatCount}</span>}
              {part.isDuplicate && (
                <RepeatIcon className="duplicate-icon" aria-label={t('songForm.part.isDuplicate')} />
              )}
              {hasMidi(part) && <PlayIcon className="midi-icon" aria-label={t('songForm.part.hasMidi')} />}
              {hasPartChart(part) && <ChordBarsIcon className="chart-icon" aria-label={t('songForm.part.hasChart')} />}
              {(part.chartIntensity || part.chartIntensityPasses || part.chartBuild) && (
                <IntensityMeter level={part.chartIntensity} className="intensity-meter" />
              )}
              {part.assignedUsers?.length > 0 && (
                <AssignedIcon className="assigned-icon" aria-label={t('songForm.part.hasAssigned')} />
              )}
              {part.assignedInstruments?.length > 0 && (
                <InstrumentsIcon
                  className="instruments-icon"
                  aria-label={t('songForm.part.instruments', { list: getInstrumentLabels(part).join(', ') })}
                />
              )}
              {/* A host part shows how many notes it carries; a note shows how many parts show it */}
              {(attachedNoteCount > 0 || attachedToCount > 0) && (
                <span
                  className="attach-badge"
                  aria-label={
                    attachedNoteCount > 0
                      ? t('songForm.part.showsNotes', { count: attachedNoteCount })
                      : t('songForm.part.attachedTo', { count: attachedToCount })
                  }
                >
                  <NoteIcon className="attach-badge-icon" />
                  {attachedToCount > 0 && attachedToCount}
                  {attachedNoteCount > 1 && attachedNoteCount}
                </span>
              )}
            </span>
          </PartTooltip>
        )}
      </span>
      {/* While a note is being edited, every part it could be attached to offers the toggle
          in place of its (hidden) options menu */}
      {canAttach && (
        <div className="controls">
          <button
            type="button"
            className={classNames('attach-pill', { attached: isAttached })}
            onClick={onToggleAttach}
            aria-pressed={isAttached}
          >
            {isAttached && <CheckIcon className="attach-pill-icon" />}
            {isAttached ? t('songForm.part.attached') : t('songForm.part.attach')}
          </button>
        </div>
      )}
      {!editPartId && (
        <div className="controls">
          <PartOptions
            part={part}
            partIndex={partIndex}
            onPartDuplicate={onPartDuplicate}
            onPartEdit={onPartEdit}
            onPartIntensity={onPartIntensity && canArrange(part) ? onPartIntensity : undefined}
            onPartDelete={onPartDelete}
            onMenuOpenChange={onMenuOpenChange}
            showDuplicateToSectionEnd={!isSection && isPartInSection(parts, part.id)}
          />
        </div>
      )}
    </li>
  );
}

export default SongPart;
